import { formatNumber } from '@trips/shared/format';
import { distanceMi, EARTH_CIRCUMFERENCE_MI } from '@trips/shared/geo';
import { type ReactNode, useMemo, useState } from 'react';
import {
  selectItemsByTrip,
  selectPlaceLookup,
  selectPrefs,
  selectTripSummaries,
} from '../../data/selectors';
import { useStore } from '../../data/store';
import { Page } from '../../ui/Page';
import { Segmented } from '../../ui/Segmented';
import s from './TravelStats.module.css';

type Period = 'all' | 'year';

/** Trips, days and nights away, flights, distance, countries and cities. */
export function TravelStats({ switcher }: { switcher: ReactNode }) {
  const trips = useStore(selectTripSummaries);
  const byTrip = useStore(selectItemsByTrip);
  const places = useStore(selectPlaceLookup);
  const prefs = useStore(selectPrefs);
  const [period, setPeriod] = useState<Period>('all');
  const year = String(new Date().getFullYear());

  const stats = useMemo(() => {
    const scoped = trips.filter(
      (t) => t.phase !== 'upcoming' && (period === 'all' || t.startDate?.startsWith(year)),
    );
    let flights = 0;
    let miles = 0;
    let nights = 0;
    let days = 0;
    const countries = new Set<string>();
    const cities = new Set<string>();
    for (const t of scoped) {
      days += t.days;
      nights += t.nights;
      for (const c of t.places) cities.add(c);
      for (const i of byTrip.get(t.id) ?? []) {
        if (i.status !== 'active') continue;
        for (const pid of [i.startPlaceId, i.endPlaceId]) {
          const p = places(pid);
          if (p?.countryCode) countries.add(p.countryCode);
        }
        if (i.type === 'flight') {
          flights++;
          const a = places(i.startPlaceId);
          const b = places(i.endPlaceId);
          if (a?.lat != null && a.lon != null && b?.lat != null && b.lon != null) {
            miles += distanceMi({ lat: a.lat, lon: a.lon }, { lat: b.lat, lon: b.lon });
          }
        }
      }
    }
    return {
      trips: scoped.length,
      flights,
      miles,
      nights,
      days,
      countries: countries.size,
      cities: cities.size,
    };
  }, [trips, byTrip, places, period, year]);

  const distance = prefs.units === 'mi' ? stats.miles : stats.miles * 1.609344;
  const tiles: [string, string, string?][] = [
    ['Trips', formatNumber(stats.trips)],
    ['Days away', formatNumber(stats.days)],
    ['Nights away', formatNumber(stats.nights)],
    ['Flights', formatNumber(stats.flights)],
    [
      prefs.units === 'mi' ? 'Miles flown' : 'Kilometers flown',
      formatNumber(distance),
      `${(stats.miles / EARTH_CIRCUMFERENCE_MI).toFixed(2)} times around the Earth`,
    ],
    ['Countries', formatNumber(stats.countries)],
    ['Cities', formatNumber(stats.cities)],
  ];

  return (
    <Page title="Stats" wide>
      {switcher}
      <Segmented
        label="Period"
        value={period}
        onChange={setPeriod}
        options={[
          { value: 'all', label: 'All time' },
          { value: 'year', label: year },
        ]}
      />
      <div className={s.tiles}>
        {tiles.map(([label, value, note]) => (
          <div key={label} className={s.tile}>
            <span className="t-footnote muted">{label}</span>
            <span className={`${s.value} num`}>{value}</span>
            {note && <span className="t-footnote muted num">{note}</span>}
          </div>
        ))}
      </div>
    </Page>
  );
}
